<script lang="ts" module>
  /**
   * Shared user shape for the header. Kept minimal — the auth layer passes
   * either null (guest) or an object with at least a display-ish field.
   */
  export interface HeaderUser {
    name?: string | null;
    displayName?: string | null;
    email?: string | null;
    avatarUrl?: string | null;
    href?: string;
  }

  /** Best-guess label for the avatar pill initial. */
  export function displayFor(u: HeaderUser): string {
    return u.name ?? u.displayName ?? u.email ?? '';
  }
</script>

<script lang="ts">
  import { page } from '$app/stores';
  import { onMount } from 'svelte';
  import LogoIcon from './LogoIcon.svelte';
  import SunIcon from '$components/ui/icons/SunIcon.svelte';
  import MoonIcon from '$components/ui/icons/MoonIcon.svelte';
  import MenuIcon from '$components/ui/icons/MenuIcon.svelte';

  interface NavLink {
    href: string;
    label: string;
  }
  const NAV: NavLink[] = [
    { href: '/search', label: 'Іздеу' },
    { href: '/learn',  label: 'Оқу' },
    { href: '/test',   label: 'Тест' }
  ];

  interface Props {
    user?: HeaderUser | null;
    /** Called from the hamburger so the layout can open its MobileMenu. */
    onOpenMobile?: () => void;
  }
  let { user = null, onOpenMobile }: Props = $props();

  // Theme state — kept local, persisted to localStorage.
  let theme = $state<'light' | 'dark'>('light');
  let isMac = $state(false);

  onMount(() => {
    theme = (localStorage.getItem('theme') as 'light' | 'dark') ?? 'light';
    document.documentElement.setAttribute('data-theme', theme);
    isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
  });

  function toggleTheme(): void {
    theme = theme === 'light' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }

  function openCommandPalette(): void {
    window.dispatchEvent(new CustomEvent('open-command-palette'));
  }

  const pathname = $derived($page.url.pathname);
  function isActive(href: string): boolean {
    if (href === '/search') return pathname === '/' || pathname.startsWith('/search');
    return pathname.startsWith(href);
  }
</script>

<header class="header">
  <div class="container">
    <div class="header-left">
      <a href="/" class="logo" aria-label="Zetix — басты бет">
        <LogoIcon size={20} />
        <span>Zetix</span>
      </a>
      <nav class="nav-desktop" aria-label="Басты навигация">
        {#each NAV as item (item.href)}
          <a
            href={item.href}
            class="nav-link"
            class:active={isActive(item.href)}
            aria-current={isActive(item.href) ? 'page' : undefined}
          >
            {item.label}
          </a>
        {/each}
      </nav>
    </div>

    <div class="header-right">
      <button
        class="cmdk-button"
        onclick={openCommandPalette}
        aria-label="Командалық панельді ашу"
        type="button"
      >
        <span class="cmdk-kbd">{isMac ? '⌘' : 'Ctrl'}</span>
        <span class="cmdk-k">K</span>
      </button>

      <button
        class="icon-btn"
        onclick={toggleTheme}
        aria-label={theme === 'dark' ? 'Күндізгі режимге ауыстыру' : 'Түнгі режимге ауыстыру'}
        type="button"
      >
        {#if theme === 'dark'}
          <SunIcon size={18} />
        {:else}
          <MoonIcon size={18} />
        {/if}
      </button>

      {#if user}
        <a href={user.href ?? '/profile'} class="profile-link" aria-label="Профиль">
          {(displayFor(user) || '?').slice(0, 1).toUpperCase()}
        </a>
      {:else}
        <a href="/auth/login" class="sign-in-btn">Кіру</a>
      {/if}

      <button
        class="mobile-menu-btn"
        onclick={() => onOpenMobile?.()}
        aria-label="Мобильді менюды ашу"
        type="button"
      >
        <MenuIcon size={20} />
      </button>
    </div>
  </div>
</header>

<style>
  .header {
    position: sticky;
    top: 0;
    z-index: var(--z-sticky);
    height: var(--header-h);
    background: var(--color-bg);
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .container {
    height: 100%;
    max-width: var(--container-max);
    margin: 0 auto;
    padding: 0 var(--space-4);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
  }
  .header-left {
    display: flex;
    align-items: center;
    gap: var(--space-6);
    min-width: 0;
  }
  .header-right {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  .logo {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--color-fg);
    font-weight: var(--fw-semibold);
    font-size: var(--text-base);
    letter-spacing: -0.01em;
    transition: opacity var(--dur-fast) var(--ease);
  }
  .logo:hover { opacity: 0.75; }

  .nav-desktop {
    display: none;
    align-items: center;
    gap: var(--space-1);
  }
  @media (min-width: 768px) {
    .nav-desktop { display: flex; }
  }

  .nav-link {
    position: relative;
    padding: var(--space-2) var(--space-3);
    font-size: var(--text-sm);
    font-weight: var(--fw-medium);
    color: var(--color-fg-secondary);
    border-radius: var(--radius-full);
    transition: background-color var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease);
    white-space: nowrap;
  }
  .nav-link:hover {
    background: var(--color-bg-muted);
    color: var(--color-fg);
  }
  .nav-link.active {
    background: var(--accent-bg);
    color: var(--accent);
  }

  .cmdk-button {
    display: none;
    align-items: center;
    gap: var(--space-1);
    height: 32px;
    padding: 0 var(--space-2);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-full);
    background: var(--color-bg-subtle);
    color: var(--color-fg-secondary);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    cursor: pointer;
    transition:
      background-color var(--dur-fast) var(--ease),
      color var(--dur-fast) var(--ease);
  }
  @media (min-width: 768px) {
    .cmdk-button { display: inline-flex; }
  }
  .cmdk-button:hover {
    background: var(--color-bg-muted);
    color: var(--color-fg);
  }
  .cmdk-kbd { font-weight: var(--fw-medium); }
  .cmdk-k { font-weight: var(--fw-medium); }

  .icon-btn {
    display: inline-grid;
    place-items: center;
    height: 32px;
    width: 32px;
    border: 0;
    border-radius: var(--radius-full);
    background: transparent;
    color: var(--color-fg);
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease);
  }
  .icon-btn:hover { background: var(--color-bg-muted); }

  .profile-link {
    display: inline-grid;
    place-items: center;
    height: 32px;
    width: 32px;
    border-radius: var(--radius-full);
    background: var(--color-bg-muted);
    color: var(--color-fg);
    font-size: var(--text-xs);
    font-weight: var(--fw-semibold);
    transition: background-color var(--dur-fast) var(--ease);
  }
  .profile-link:hover { background: var(--color-border); }

  .sign-in-btn {
    display: none;
    align-items: center;
    height: 32px;
    padding: 0 var(--space-4);
    border-radius: var(--radius-full);
    background: var(--color-fg);
    color: var(--color-bg);
    font-size: var(--text-sm);
    font-weight: var(--fw-medium);
    transition: opacity var(--dur-fast) var(--ease);
  }
  @media (min-width: 768px) {
    .sign-in-btn { display: inline-flex; }
  }
  .sign-in-btn:hover { opacity: 0.85; }

  .mobile-menu-btn {
    display: inline-grid;
    place-items: center;
    height: 32px;
    width: 32px;
    border: 0;
    border-radius: var(--radius-full);
    background: transparent;
    color: var(--color-fg);
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease);
  }
  .mobile-menu-btn:hover { background: var(--color-bg-muted); }
  @media (min-width: 768px) {
    .mobile-menu-btn { display: none; }
  }

  @media (prefers-reduced-motion: reduce) {
    .header *, .nav-link, .icon-btn, .cmdk-button, .profile-link, .sign-in-btn {
      transition: none !important;
    }
  }
</style>
